import {inject, Injectable, resource} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Observable} from 'rxjs';
import {take} from 'rxjs/operators';
import {ResponseWrapper} from '../../suppliers/wrappers/response-wrapper';
import {GraphQLService} from '../technique/graphql.service';
import {HttpService} from '../technique/http.service';
import {Auth2Service} from '../security/auth2.service';
import {FermetureJourSemaineDTO} from '../../dtos/client/fermeture/fermeture-jour-semaine-dto';
import {JourSemaine} from '../../enums/client/jour-semaine.enum';
import {FermeturePeriodeDTO} from '../../dtos/client/fermeture/fermeture-periode-dto';
import {FermetureClientHierarchyDTO} from '../../dtos/client/fermeture/fermeture-client-hierarchy-dto';
import {MotifFermetureDTO} from '../../dtos/client/fermeture/motif-fermeture-dto';
import {SaveFermetureJourSemaineResultDTO} from '../../dtos/client/fermeture/save-fermeture-jour-semaine-result-dto';
import {SaveFermeturePeriodeResultDTO} from '../../dtos/client/fermeture/save-fermeture-periode-result-dto';
import {UtilsService} from '../../utils/utils.service';

@Injectable({providedIn: 'root'})
export class FermetureClientService {

  private readonly graphQlSvc = inject(GraphQLService);
  private readonly httpSvc = inject(HttpService);
  private readonly auth2Svc = inject(Auth2Service);
  private readonly utils = inject(UtilsService);

  readonly #environnement = toSignal(this.auth2Svc.environnement$);

  readonly fermeturesJourResource = resource({
    params: () => this.#environnement(),
    loader: async () => {
      const siteIds: number[] = this.auth2Svc.utilisateur.siteListLocaux.map(s => s.id);
      const response: any = await this.graphQlSvc.sendQuery(`
        {
          allFermeturesJourSemaine(filters: { siteIds: [${siteIds}] }) {
            cmcPlcId, clientId, clientLibelle,
            pointLivraisonId, pointLivraisonLibelle,
            prestationId, prestationLibelle, jours
          }
        }
      `).pipe(take(1)).toPromise();
      return (response?.allFermeturesJourSemaine ?? []) as FermetureJourSemaineDTO[];
    }
  });

  readonly fermeturesPeriodeResource = resource({
    params: () => this.#environnement(),
    loader: async () => {
      const siteIds: number[] = this.auth2Svc.utilisateur.siteListLocaux.map(s => s.id);
      const response: any = await this.graphQlSvc.sendQuery(`
        {
          allFermeturesPeriode(filters: { siteIds: [${siteIds}] }) {
            id, cmcPlcId, clientId, clientLibelle,
            pointLivraisonId, pointLivraisonLibelle,
            prestationId, prestationLibelle, dateDebut, dateFin,
            motifId, motifLibelle
          }
        }
      `).pipe(take(1)).toPromise();
      return (response?.allFermeturesPeriode ?? []) as FermeturePeriodeDTO[];
    }
  });

  readonly motifsFermetureResource = resource({
    params: () => this.#environnement(),
    loader: async () => {
      const response: any = await this.graphQlSvc.sendQuery(`
        {
          allMotifsFermeture {
            id, libelle
          }
        }
      `).pipe(take(1)).toPromise();
      return (response?.allMotifsFermeture ?? []) as MotifFermetureDTO[];
    }
  });

  readonly hierarchyResource = resource({
    params: () => this.#environnement(),
    loader: async () => {
      const response: any = await this.httpSvc.get('dolrest/gestion-clients/fermeture/hierarchy').pipe(take(1)).toPromise();
      return response as FermetureClientHierarchyDTO;
    }
  });

  getFermeturesPeriodeByFilters = (plcIdList: number[], prestationIdList: number[], dateDebut: string, dateFin: string): Promise<FermeturePeriodeDTO[]> => {
    const siteIdList: number[] = this.auth2Svc.utilisateur.siteListLocaux.map(s => s.id);
    return this.graphQlSvc.sendQuery(`
      {
        allFermeturesPeriode(filters: {
          siteIds: [${siteIdList}],
          plcIdList: [${plcIdList}],
          prestationIdList: [${prestationIdList}],
          dateDebut: "${dateDebut}",
          dateFin: "${dateFin}"
        }) {
          id, cmcPlcId, pointLivraisonId, prestationId, dateDebut, dateFin
        }
      }
    `).pipe(take(1)).toPromise()
      .then(response => (response?.allFermeturesPeriode ?? []) as FermeturePeriodeDTO[]);
  };

  getFermeturesJourSemaineByFilters = (plcIdList: number[], prestationIdList: number[]): Promise<FermetureJourSemaineDTO[]> => {
    const siteIdList: number[] = this.auth2Svc.utilisateur.siteListLocaux.map(s => s.id);
    return this.graphQlSvc.sendQuery(`
      {
        allFermeturesJourSemaine(filters: {
          siteIds: [${siteIdList}],
          plcIdList: [${plcIdList}],
          prestationIdList: [${prestationIdList}]
        }) {
          cmcPlcId, pointLivraisonId, prestationId, jours
        }
      }
    `).pipe(take(1)).toPromise()
      .then(response => (response?.allFermeturesJourSemaine ?? []) as FermetureJourSemaineDTO[]);
  };

  isFerme = (date: Date, plcId: number, prestationId: number, fermeturePeriodeList: FermeturePeriodeDTO[], fermetureJourSemaineList: FermetureJourSemaineDTO[]): boolean => {
    const d = new Date(date);
    const dateStr = this.utils.toLocalDate(d);
    const jourSemaine: JourSemaine = d.getDay() === 0 ? 7 : d.getDay();
    const fermeParPeriode = fermeturePeriodeList?.some(f =>
      f.pointLivraisonId === plcId &&
      f.prestationId === prestationId &&
      dateStr >= this.utils.toLocalDate(new Date(f.dateDebut)) && dateStr <= this.utils.toLocalDate(new Date(f.dateFin))
    ) ?? false;
    const fermeParJour = fermetureJourSemaineList?.some(f =>
      f.pointLivraisonId === plcId &&
      f.prestationId === prestationId &&
      f.jours?.includes(JourSemaine[jourSemaine] as any)
    ) ?? false;
    return fermeParPeriode || fermeParJour;
  };

  saveFermeturesJour = (list: FermetureJourSemaineDTO[]): Observable<ResponseWrapper<SaveFermetureJourSemaineResultDTO>> =>
    this.httpSvc.post('dolrest/gestion-clients/fermeture/fermeture-jour', list);

  deleteFermeturesJour = (ids: number[]): Observable<ResponseWrapper<void>> =>
    this.httpSvc.post('dolrest/gestion-clients/fermeture/fermeture-jour/delete', ids);

  saveFermeturesPeriode = (list: FermeturePeriodeDTO[]): Observable<ResponseWrapper<SaveFermeturePeriodeResultDTO>> =>
    this.httpSvc.post('dolrest/gestion-clients/fermeture/fermeture-periode', list);

  deleteFermeturesPeriode = (ids: number[]): Observable<ResponseWrapper<void>> =>
    this.httpSvc.post('dolrest/gestion-clients/fermeture/fermeture-periode/delete', ids);
}
